// ---- vocabularies
export const QG_SEVERITIES = ["high", "medium", "low"];
export const QG_RULE_KINDS = [
    "section_filled",
    "required_mention",
    "attribute_fits_content",
    "uniqueness",
    "judge_question",
];
export const QG_SECTION_FILLED_MODES = ["required", "expected", "element_empty"];
export const QG_ENGINES = ["jev", "classic"];
/** The rule template's provenance line; unrelated to a finding's source. */
export const QG_RULE_SOURCES = ["v33", "v40", "industry", "research", "new"];
/** `element` is a whole-element rule scope; `entity` and `project` are the count rows. */
export const QG_SCOPE_KINDS = ["section", "tab", "element", "entity", "project"];
/** `empty`: a section with no content and no open finding, never green, since nothing was checked. */
export const QG_SECTION_STATES = [
    "green",
    "low",
    "medium",
    "high",
    "pending",
    "not_checked",
    "empty",
];
export const QG_EVALUATION_STATUSES = ["queued", "checking", "done", "failed"];
export const QG_MANIFEST_OUTCOMES = [
    "pending",
    "running",
    "evaluated",
    "inapplicable",
    "failed",
];
export const QG_SUMMARY_STATUSES = ["current", "partial", "outdated", "writing", "failed"];
export const QG_FINDING_STATES = ["open", "dismissed", "resolved"];
/** Only `guard` is written; `documents` and `review` are reserved. */
export const QG_FINDING_SOURCES = ["guard", "documents", "review"];
/** One owner row per (scope, ruleId); one pin row per counterpart scope. */
export const QG_FINDING_ROLES = ["owner", "pin"];
/** `not an issue` and `known, accepted`. */
export const QG_DISMISSAL_TAGS = ["not_an_issue", "known_accepted"];
export const QG_DISMISSAL_STATUSES = ["current", "superseded"];
export const QG_NOT_ASSESSED_REASONS = [
    "no_answer",
    "malformed",
    "oversize",
    "call_failed",
    "fact_unavailable",
    "context_too_large",
    /** A family 5 proposal the classic engine answered outside its shape or its roster. */
    "proposal_malformed",
    /** The rule set's pack was building or its build failed when the element's attempt ran. */
    "pack_unavailable",
];
/**
 * Why a judged rule read inapplicable where its manifest records it: an extraction of the
 * scope's own fact (this section, or this element's own sections) read empty, or one of a
 * counterpart a Condition judge clause says states the fact.
 */
export const QG_INAPPLICABLE_REASONS = ["own_fact_empty", "counterpart_fact_empty"];
/** `not_assessed` is a processing failure, never doubt. */
/** A rule's answer: a finding always carries its rule's severity. */
export const QG_ANSWER_STATES = ["ok", "finding", "inapplicable", "not_assessed"];
export const QG_TRIGGERS = [
    "save",
    "attribute_edit",
    "create",
    "move",
    "rename",
    "delete",
    "restore",
    "mention_change",
    "release_change",
    "scan",
    /** An admin's re-judge of an element or one scope, past the verdict cache: never a Scan. */
    "rejudge",
];
/**
 * How a rule its first two readings did not settle is decided: `recheck` asks the recheck
 * question, two of three readings deciding; `escalation` asks the classic engine for a cited
 * verdict and the judge whether the quotes support it, the recheck alone deciding when it cannot.
 */
export const QG_SETTLE_MODES = ["recheck", "escalation"];
/** `escalate` is the classic engine's reading in the escalation pass, apart from the judge's. */
export const QG_USAGE_KINDS = [
    "judge",
    "summary",
    "generator",
    "extract",
    "escalate",
    "probe",
    "propose",
];
/**
 * `confirm` is a judge question's second form: a differently formed reading of the same defect,
 * asked only when the first form reads above the clear threshold. `recheck` is its third form,
 * formed differently from both, asked only when the first two neither clear nor raise the rule.
 */
export const QG_JEV_QUESTION_ROLES = [
    "locate",
    "defect",
    "contrast",
    "applicability",
    "confirm",
    "recheck",
];
/**
 * The passes of one judgment: the first reading, the confirmation of a rule read above the clear
 * threshold, the recheck of a rule its two forms did not settle, the per-candidate follow-up, and
 * the escalation's check of a cited verdict, which settles in the recheck's place when the settle
 * mode is `escalation`; for a family 5 rule, the first reading over the roster (`candidates`) and
 * the confirmation of each surfaced candidate with its recheck (`candidate_confirmation`).
 */
export const QG_JUDGE_PASSES = [
    "first",
    "confirmation",
    "recheck",
    "follow_up",
    "escalation",
    "candidates",
    "candidate_confirmation",
];
/** A noul is a yes/no question answered with a probability. */
export const QG_JEV_PRIMITIVES = ["noul", "choice"];
/** `|` separates the parts of a scope key, so a `|` or `\` inside a part is escaped with `\`. */
function scopePart(value) {
    return value.replace(/[\\|]/g, (char) => `\\${char}`);
}
/** The one string form of a scope, stored as `scopeKey` and indexed; distinct scopes never share it. */
export function scopeKey(scope) {
    switch (scope.kind) {
        case "section":
            return ["section", scope.entityId, scope.tabKey, scope.fieldId].map(scopePart).join("|");
        case "tab":
            return ["tab", scope.entityId, scope.tabKey].map(scopePart).join("|");
        case "element":
            return ["element", scope.entityId].map(scopePart).join("|");
        case "entity":
            return ["entity", scope.entityId].map(scopePart).join("|");
        case "project":
            return ["project", scope.projectId].map(scopePart).join("|");
    }
}
/** Which of a rule's own examples a probe reading is of. */
export const QG_PROBE_EXAMPLE_KINDS = ["pass", "fail", "nearMiss"];
/**
 * The definition probe: a Jev definition asked on its rule's own examples, each example alone as
 * the section's content, on the pinned judge, once per definition and examples (`key`). The
 * readings are stored raw and held to the thresholds in force when the rule is published.
 */
/** Why a probe step did not ask a definition on its examples. */
export const QG_PROBE_SKIP_KINDS = [
    "project_data",
    "no_examples",
    "no_defect_question",
    "not_configured",
    "no_definition",
];
/**
 * What a mention rule wants of a section, in the frame its question was written in: an element
 * named as plain text (`mention_named`), an element referred to in other words
 * (`mention_described`), or a mention the section must hold (`mention_required`).
 */
export const QG_MENTION_FRAMES = [
    "mention_named",
    "mention_described",
    "mention_required",
];
